// THE design tokens file (FR-020, FR-021, FR-023; constitution VII, X; data-model §8).
// 2026-10-15 second redesign: Live Colour Tiles (PRODUCT.md, Brand Commitments). Flat tiles in the four
// Akaiva colours, square corners, no shadows; errors have their own `danger` role.
// Every text pair below is tested at 4.5:1 in both themes. Change values here, never in components.

const light = {
  background: "#FFFFFF", // ground: white; tiles carry the colour
  surface: "#F3F4F6", // flat panels (forms, lists); no borders, no shadow
  surfaceAlt: "#E9EBEF",
  border: "#D3D6DD",
  text: "#0E1015",
  textMuted: "#5F6573",
  primary: "#0B7B9C", // Akaiva blue tile, deepened for white text
  primaryText: "#086580",
  primarySoft: "#E1F2F7",
  accent: "#0E1015",
  onAccent: "#FFFFFF",
  success: "#1F7A45",
  successSoft: "#E3F3E9",
  warning: "#8A5A00",
  warningSoft: "#FFF3D6",
  info: "#086580",
  infoSoft: "#E1F2F7",
  danger: "#B3261E",
  dangerSoft: "#FCE8E6",
  // Category tints (soft square + glyph), kept for small inline use.
  tintAmber: "#8A5A00",
  tintAmberSoft: "#FFF3D6",
  tintGreen: "#1F7A45",
  tintGreenSoft: "#E3F3E9",
  tintPink: "#A8325A",
  tintPinkSoft: "#FCE7EE",
  // Live tiles: the four Akaiva colours as flat fills, each with its own ink.
  tileBlue: "#0B7B9C",
  tilePink: "#C93A64",
  tileGreen: "#23854B",
  tileAmber: "#F2A900",
  onTile: "#FFFFFF",
  onTileAmber: "#111111",
  tileInk: "#0E1015",
  tileMuted: "#FBE3EA",
  onTileMuted: "#8E2A4D",
  focusRing: "#086580",
  crestBackground: "#0B7B9C",
  crestText: "#FFFFFF",
  envBadge: "#FFF3D6",
  envBadgeText: "#8A5A00",
  offline: "#E1F2F7",
  offlineText: "#086580",
  badge: "#C93A64",
  badgeText: "#FFFFFF",
  scrim: "rgba(0, 0, 0, 0.55)",
  // Signed-out and account screens: the Akaiva sign-in frame shared with the admin console and the
  // driver app (constitution 1.1.0): backdrop, its lit corner, wave lines, the card and its edge.
  scene: "#E6EEF7",
  sceneHi: "#FFFFFF",
  sceneLine: "#B9CBE0",
  sceneStar: "#FFFFFF",
  sceneCard: "#FFFFFF",
  sceneEdge: "#DCE5F0",
  /** The admin console's sign-in red and link blue, so every Akaiva sign-in looks the same. */
  scenePrimary: "#C8283A",
  sceneLink: "#2A56A0",
};

type Palette = { readonly [K in keyof typeof light]: string };

const dark: Palette = {
  background: "#000000", // true black: tiles glow on it, and OLED phones save power
  surface: "#111318",
  surfaceAlt: "#1B1E25",
  border: "#2A2E37",
  text: "#FFFFFF",
  textMuted: "#9AA0AE",
  primary: "#0B7B9C",
  primaryText: "#1AA3C6", // the raw mark blue reads on black
  primarySoft: "#072A35",
  accent: "#FFFFFF",
  onAccent: "#000000",
  success: "#6FD69B",
  successSoft: "#16301F",
  warning: "#F5C451",
  warningSoft: "#33290F",
  info: "#1AA3C6",
  infoSoft: "#072A35",
  danger: "#FFB4AB",
  dangerSoft: "#3A1D1B",
  tintAmber: "#F5C451",
  tintAmberSoft: "#33290F",
  tintGreen: "#6FD69B",
  tintGreenSoft: "#16301F",
  tintPink: "#F48FB1",
  tintPinkSoft: "#3A1B27",
  tileBlue: "#0B7B9C",
  tilePink: "#C93A64",
  tileGreen: "#23854B",
  tileAmber: "#F2A900",
  onTile: "#FFFFFF",
  onTileAmber: "#111111",
  tileInk: "#1B1E25",
  tileMuted: "#5A2438",
  onTileMuted: "#F6B8CA",
  focusRing: "#1AA3C6",
  crestBackground: "#0B7B9C",
  crestText: "#FFFFFF",
  envBadge: "#33290F",
  envBadgeText: "#F5C451",
  offline: "#072A35",
  offlineText: "#1AA3C6",
  badge: "#C93A64",
  badgeText: "#FFFFFF",
  scrim: "rgba(0, 0, 0, 0.7)",
  scene: "#071530",
  sceneHi: "#1C4F8F",
  sceneLine: "#3F7BD0",
  sceneStar: "#DCE8FF",
  sceneCard: "#0D1B36",
  sceneEdge: "#2C4572",
  scenePrimary: "#D63A4B",
  sceneLink: "#93B4F2",
};

export type ColorRole = keyof typeof light;
export type ThemeName = "light" | "dark";

export const tokens = {
  color: {
    light: light as Palette,
    dark,
    /** Button text on `pr` is always white (both themes). */
    onPrimary: "#FFFFFF",
    /** Akaiva mark and category accents, same in both themes (PRD §7). */
    brand: {
      a1: "#E4577A",
      a2: "#F2A900",
      a3: "#2F9E5B",
      a4: "#1AA3C6",
      a5: "#F07A28",
    },
  },
  /** Card shadow `sh` (React Native `boxShadow` string, supported on both targets). */
  shadow: {
    light: {
      card: "0 0 0 transparent",
    },
    dark: { card: "0 0 0 transparent" },
  },
  font: {
    /** One system family: Roboto on Android, the device's UI face on the web. Nothing to download. */
    family:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    weight: {
      light: "300",
      regular: "400",
      medium: "500",
      semiBold: "600",
      bold: "700",
    },
  },
  /** px. `base` 14 is the body size. */
  fontSize: {
    xxs: 11,
    xs: 12,
    sm: 13,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    x2l: 20,
    x3l: 24,
    x4l: 28,
    x5l: 32,
    x6l: 40,
    x7l: 48,
    x8l: 64,
    x9l: 88,
  },
  /** Line-height multiplier; use `lineHeightFor(size)` for React Native's absolute value. */
  lineHeight: { base: 1.4 },
  /** 006: letter spacing, px (the join code is shown spaced, FR-018). */
  letterSpacing: {
    code: 6,
    /** Poster-size display lines (the child's name on Home) tighten. */
    display: -2,
  },
  /** Gaps, px. */
  space: {
    xxs: 4,
    xs: 6,
    sm: 8,
    smd: 10,
    md: 12,
    lg: 16,
    xl: 20,
    x2l: 24,
    x3l: 32,
  },
  /**
   * Softened tile world (2026-10-15): tiles and panels 16, controls 12, small marks 10; `pill` stays
   * for dots and badges.
   */
  radius: {
    card: 16,
    panel: 16,
    softPanel: 12,
    button: 12,
    buttonSm: 10,
    buttonLg: 14,
    input: 12,
    mark: 10,
    pill: 99,
  },
  borderWidth: {
    card: 1,
    input: 2,
    focusRing: 2 /* derived: visible focus outline */,
  },
  size: {
    touchTarget: 44,
    button: 44,
    buttonSm: 36,
    buttonLg: 52,
    input: 44,
    iconButton: 44,
    chip: 26,
    tab: 38,
    headerHeightDesktop: 72,
    avatar: 40,
    badge: 20,
    icon: { sm: 16, md: 20, lg: 26, xl: 40 },
    iconStroke: 1.8,
    navUnderline: 3,
    crest: 40, // derived: avatar size
    sideNav: 248, // 006: school-admin side navigation (board SABanners)
    qrPreview: 176, // 006: on-screen QR preview
  },
  /** Web only: at or above this width the desktop chrome is used (research R7). */
  breakpoint: { desktop: 1024 },
  /** Text/background pairs used by the shell; tested at 4.5:1 or more in both themes (SC-005). */
  contrastPairs: [
    ["text", "background"],
    ["text", "surface"],
    ["textMuted", "background"],
    ["textMuted", "surface"],
    ["primaryText", "background"],
    ["primaryText", "surface"],
    ["primaryText", "primarySoft"],
    ["onAccent", "accent"],
    ["crestText", "crestBackground"],
    ["envBadgeText", "envBadge"],
    ["offlineText", "offline"],
    ["badgeText", "badge"],
    // 002 forms and messages (SC-010): field error text in prt, the inline message tones.
    ["success", "successSoft"],
    ["warning", "warningSoft"],
    ["info", "infoSoft"],
    ["danger", "dangerSoft"],
    ["danger", "surface"],
    ["primaryText", "surfaceAlt"],
    ["tintAmber", "tintAmberSoft"],
    ["tintGreen", "tintGreenSoft"],
    ["tintPink", "tintPinkSoft"],
    ["onTile", "tileBlue"],
    ["onTile", "tilePink"],
    ["onTile", "tileGreen"],
    ["onTileAmber", "tileAmber"],
    ["onTile", "tileInk"],
    ["onTileMuted", "tileMuted"],
    // The shared sign-in frame: text on the card and on the backdrop.
    ["text", "sceneCard"],
    ["textMuted", "sceneCard"],
    ["primaryText", "sceneCard"],
    ["danger", "sceneCard"],
    ["text", "scene"],
    ["textMuted", "scene"],
    ["sceneLink", "sceneCard"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
  /**
   * Non-text pairs (borders, outlines), tested at 3:1 or more (WCAG 1.4.11): selection borders in
   * `primary`, the focus ring, and the field error border in `danger`.
   */
  nonTextContrastPairs: [
    ["primary", "surface"],
    ["focusRing", "surface"],
    ["danger", "surface"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
} as const;

export type Tokens = typeof tokens;

/** Absolute line height for a font size (React Native needs px). */
export function lineHeightFor(
  fontSize: number,
  multiplier: number = tokens.lineHeight.base,
): number {
  return Math.round(fontSize * multiplier);
}
